<template>
    <div class="comp-item-quick-view">
        
        <div v-if="hasCompItem" class="comp-item-quick-view-sections">

            <div class="section-item">
                <div class="d-flex gap-3 align-items-end justify-content-between w-100">
                    <div class="">
                        <h6 class="text-muted mt-0 mb-2">Document Type</h6>
                        <h3 class="mb-0">{{ compItem.title }}</h3>
                    </div>
                    <div class="d-flex gap-2">
                        <b-button
                            :href="compItem.urls.internal_detail"
                            variant="subtle-secondary" 
                            size="sm" 
                            class="text-nowrap"
                        >
                            <b-icon icon="list-task" aria-hidden="true"></b-icon>
                            View Status
                        </b-button> 
                        <b-button
                            v-if="canManageItem"
                            :href="compItem.urls.internal_setup"
                            variant="subtle-secondary" 
                            size="sm" 
                            class="text-nowrap"
                        >
                            <b-icon icon="gear-fill" aria-hidden="true"></b-icon>
                            Manage
                        </b-button>
                        <!-- turning off dropdown actions for now since you removed all these, moved to other specific action locations -->
                        <b-dropdown v-if="false" text="Actions" variant="subtle-secondary" size="sm" right @show="loadTransitions">
                            <template #button-content>
                                <b-icon icon="lightning-charge-fill" variant="warning" aria-hidden="true"></b-icon>
                                Actions
                            </template>
                            <!-- set all to the same width, and less than the max for the dropdown -->
                            <template v-if="!transitionsLoaded || loadingTransitions">
                                <b-dropdown-item disabled class="width-300">
                                    <b-spinner small class="mr-1"></b-spinner>
                                    Loading...
                                </b-dropdown-item>
                            </template>
                            <template v-else-if="actionTransitions.length === 0">
                                <b-dropdown-item disabled class="width-300 text-muted special-text font-size-70">No Actions Available</b-dropdown-item>
                            </template>
                            <template v-else>
                                <b-dropdown-item 
                                    v-for="trans in actionTransitions" 
                                    :key="trans.trigger"
                                    link-class="px-2 d-flex align-items-center gap-2" 
                                    class="width-300"
                                    @click="startTransition(trans)"
                                >
                                    <b-icon 
                                        :icon="trans.icon" 
                                        :variant="trans.color_variant" 
                                        class="rounded-circle" 
                                        :class="['bg-' + (trans.color_variant || 'primary') + '-100']" 
                                        style="padding: 6px;" 
                                        font-scale="2"
                                    ></b-icon>
                                    <div class="text-wrap line-height-110">
                                        <div class="font-weight-bold font-size-80">{{ trans.label }}</div>
                                        <div class="text-muted font-size-70">{{ trans.description }}</div>
                                    </div>
                                </b-dropdown-item>
                            </template>
                        </b-dropdown>
                    </div>
                </div>
            </div>
        
            <div class="section-item flex-wrap justify-content-around">
                <div class="w-50">
                    <h6 class="item-heading">Document Category</h6>
                    <p class="mb-0 item-text">{{ compItem.item_type.name }}</p>
                </div>
                <div class="w-30">
                    <h6 class="item-heading">Kind of Document</h6>
                    <p class="mb-0 item-text">
                        <CompKindChip :kind="compItem.kind"></CompKindChip>
                    </p>
                </div>
            </div>

            <div class="section-item flex-wrap justify-content-around">
                <div class="">
                    <h6 class="item-heading">Documents Per Vendor</h6>
                    <div class="">
                        <CompSingleMultipleChip 
                            :key="compItem.allow_vendor_scopes"
                            :value="compItem.allow_vendor_scopes ? 'multiple' : 'single'"
                            class="animate__animated animate__fadeIn animate__fast"
                        ></CompSingleMultipleChip>
                    </div>
                </div>
                <div class="">
                    <h6 class="item-heading">Created</h6>
                    <p class="mb-0 item-text">{{ formatDate(compItem.created, '-') }}</p>
                </div>
                <div class="">
                    <h6 class="item-heading">Last Modified</h6>
                    <p class="mb-0 item-text">{{ formatDate(compItem.modified, '-') }}</p>
                </div>
            </div>

            <div class="section-item">
                <div class="w-100">
                    <div class="mb-2 d-flex align-items-center">
                        <h6 class="item-heading mb-0">Vendor Document Status</h6>
                        <b-icon 
                            icon="info-circle" 
                            variant="secondary" 
                            v-b-popover.hover="`Summary of Documents across all vendors who have this Document Type.`" 
                            class="ml-2"
                        ></b-icon>
                    </div>
                    <div class="w-100 d-flex">
                        <!-- <div class="w-100 border rounded-lg px-3 py-2 d-flex align-items-center justify-content-between bg-white">
                            <div class="w-25 text-center">
                                <h6 class="item-heading">Valid</h6>
                                <p class="mb-0 item-text">0 vendors</p>
                            </div>
                            <div class="w-25 text-center">
                                <h6 class="item-heading">Expired</h6>
                                <p class="mb-0 item-text">0 vendors</p>
                            </div>
                            <div class="w-25 text-center">
                                <h6 class="item-heading">Pending</h6>
                                <p class="mb-0 item-text">0 vendors</p>
                            </div>
                            <div class="w-25 text-center">
                                <h6 class="item-heading">Missing</h6>
                                <p class="mb-0 item-text">0 vendors</p>
                            </div>
                        </div> -->
                        <div v-if="statusListLoaded" key="statusListLoaded" class="w-100 border rounded-lg px-3 py-2 d-flex align-items-center justify-content-around bg-white">
                            <div v-for="vsg in vendorStatusGroups" :key="vsg.key" class="text-center">
                                <h6><b-badge :variant="vsg.variant" class="text-uppercase">{{ vsg.label }}</b-badge></h6>
                                <p class="mb-0 item-text">{{ vsg.count }}</p>
                            </div>
                            <div v-if="!vendorStatusGroups.length" class="text-center text-muted text-uppercase font-size-70">
                                No vendors have documents of this type yet.
                            </div>
                        </div>
                        <div v-else key="statusListNotLoaded" class="w-100">
                            <b-skeleton height="2rem" width="100%"></b-skeleton>
                        </div>
                    </div>
                </div>
            </div>

            <div class="section-item">
                <div class="w-100">
                    <div class="mb-2 d-flex align-items-center">
                        <h6 class="item-heading mb-0">Visibility Rules</h6>
                        <!-- add comment about 'in addition to any users with specific role' once you have Object roles working -->
                        <b-icon 
                            icon="info-circle" 
                            variant="secondary" 
                            v-b-popover.hover="`Visibility rules are currently only configurable by contacting Supplios Support.`" 
                            class="ml-2"
                        ></b-icon>
                    </div>
                    <div class="w-100 d-flex">
                        <div class="w-100 border rounded-lg px-3 py-2 d-flex align-items-center justify-content-between bg-white">
                            <div class="w-50 text-center">
                                <h6 class="item-heading">Internal Users</h6>
                                <p class="mb-0 item-text">{{ compItem.internal_roles_subs_visible_label }}</p>
                            </div>
                            <div class="w-50 text-center">
                                <h6 class="item-heading">Vendor Users</h6>
                                <p class="mb-0 item-text">{{ compItem.external_roles_subs_visible_label }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="compItem.instructions" class="section-item">
                <div class="w-100">
                    <h6 class="item-heading">Internal Notes</h6>
                    <p class="mb-0 item-text">{{ compItem.notes || '-' }}</p>
                </div>
            </div>

            <!-- <div v-if="compItem.instructions" class="section-item">
                <div class="w-100">
                    <h6 class="item-heading">Instructions for Suppliers</h6>
                    <p class="mb-0 item-text">{{ compItem.instructions || '-' }}</p>
                </div>
            </div> -->

            <div v-if="flowObjectLoaded && activeEasNodes.length" class="section-item">
                <div class="w-100">
                    <div class="mb-2 d-flex align-items-center">
                        <h6 class="item-heading mb-0">Active Reminders</h6>
                        <b-icon 
                            icon="info-circle" 
                            variant="secondary" 
                            v-b-popover.hover="`Currently active reminders.`" 
                            class="ml-2"
                        ></b-icon>
                    </div>
                    
                    <!-- SHOW LIST OF NODES HERE -->
                    <div class="d-flex flex-column gap-1">
                        <div 
                            v-for="node in activeEasNodes"
                            :key="node.id"
                            class="font-size-70 w-100 border rounded bg-gray-50 px-3 py-1"
                        >
                            <!-- <b-button variant="subtle-primary" size="sm" block class="font-size-70" v-b-popover.hover="node.description"> -->
                            <!-- NOTE: use buttons later when they will actually do something like load the node -->
                            <div class="w-100 font-size-70 d-flex align-items-center justify-content-center">
                                <b-iconstack class="mr-1" font-scale="1.25">
                                    <b-icon icon="circle-fill" class="text-primary-200"></b-icon>
                                    <b-icon icon="circle-fill" animation="throb" variant="primary" scale="0.5"></b-icon>
                                </b-iconstack>
                                <div class="font-weight-500">{{ node.title }}</div>
                            </div>
                            <div v-if="node.description" class="text-muted mt-1 font-size-70 text-center">{{ node.description }}</div>
                            <!-- </b-button> -->
                        </div>
                    </div>

                </div>
            </div>

            <div v-if="showEasManager" class="section-item">
                <div class="w-100">
                    <div class="mb-2 d-flex align-items-center">
                        <h6 class="item-heading mb-0">Default Reminder Actions</h6>
                        <b-icon 
                            icon="info-circle" 
                            variant="secondary" 
                            v-b-popover.hover="`Default reminders apply to ALL Documents for this Document Type, and apply in addition to any vendor-specific reminders.  Reminders are only sent for Documents with an expiration date.`" 
                            class="ml-2"
                        ></b-icon>
                    </div>                    
                    <!-- NOTE: we should show this as read-only here, for any edits, go to manage page -->
                    <!-- <b-alert :show="hasExpireActionSpecs && !hasExpireDate" variant="danger" class="font-size-80">
                        Reminder actions will not be triggered if there is no expiration date set for the active version of this document.
                    </b-alert> -->  <!-- check if this comp item has expiration tracking... if not, then we must disable it -->
                    <ExpireActionManager
                        :compItemId="compItemId"
                        :readOnly="true"
                        :narrow="true"
                        @updated="handleExpireActionManagerUpdated"
                    ></ExpireActionManager>
                </div>
            </div>
        </div>

        <b-modal 
            v-model="transitionModalShowing"
            centered
            hide-header
            hide-footer
            :size="activeTransition.modalSize || 'sm'"
            @shown="handleTransitionModalShown"
            @hidden="handleTransitionModalHidden"
        >
            <FlowObjectTransitionForm
                ref="transitionForm"
                :flowObject="flowObject"
                :trigger="activeTransitionTrigger"
                :autoStart="true"
                :noReloadOnSuccess="true"
                @loading-start="handleTransitionFormEvent"
                @loading-end="handleTransitionFormEvent"
                @loading-error="handleTransitionFormEvent"
                @transition-start="handleTransitionFormEvent"
                @transition-error="handleTransitionFormEvent"
                @transition-success="handleTransitionFormEvent"
                @transition-end="handleTransitionFormEvent"
                @cancel="handleTransitionFormCancel"
            ></FlowObjectTransitionForm>
        </b-modal>

    </div>
</template>

<script>
import { suppliosApi } from '@/js/api-service';
import { errorHandling, currentUserInfoMixin } from '@/js/custom-vue-mixins';
import CompKindChip from '../shared/CompKindChip.vue';
import CompSingleMultipleChip from '../shared/CompSingleMultipleChip.vue';
import { FormatDateHelper, luxonDurationToHumanizedString, isFutureDate } from '@/js/filters-helpers';
import { Duration } from 'luxon';
import ExpireActionManager from '../ExpireActionManager.vue';
import { FlowObject } from '@/components/flows/models/flowObject';
import { ComplianceItem, VendorCompItemStatus } from '../shared/compModels';
import groupBy from 'lodash/groupBy';
import FlowObjectTransitionForm from '@/components/flows/components/FlowObjectTransitionForm.vue';

// TODO - MUST UPDATE THIS TO USE FLOW OBJECT OF VCI, NOT COMP ITEM

export default {
    name: 'CompItemQuickView',
    components: {
        CompKindChip,
        CompSingleMultipleChip,
        ExpireActionManager,
        FlowObjectTransitionForm,
    },
    mixins: [
        errorHandling,
        currentUserInfoMixin,
    ],
    props: {
        compItemId: {
            type: String,
            required: true,  // make false later when we support CompItem/Vendor combos
        },
    },
    data() {
        return {
            compItem: {},  // ComplianceItem object
            loading: false,
            vciStatusList: [],  // list of VendorCompItemStatus objects
            loadingStatusList: false,
            statusListLoaded: false,
            flowObject: null,  // set in created hook -- this is for the VendorCompItem itself
            transitionsLoaded: false,
            showEasManager: true,
            // TRANSITIONS
            transitionModalShowing: false,
            activeTransitionTrigger: '',
            activeTransition: {},  // the transition object passed up from the flow transition form component
        }
    },
    computed: {
        hasCompItem() {  // loaded yet?
            return !!this.compItem?.id;
        },
        hasExpireActionSpecs() {  // NOTE: the expire action manager actually manages the expire_action_specs -- but the number from statusObj should be correct since it refreshes
            const specQty = this.compItem?.expire_action_specs?.length || 0;
            return specQty > 0;
        },
        flowObjectLoaded() {
            //return Object.keys(this.reqData).length > 0;
            return this.flowObject.hasData
        },
        flowObjectData() {
            return this.flowObject.data;
        },
        possibleTriggerNames() {
            // return this.transitions.map((transition) => {
            //     return transition.trigger;
            // });
            return this.flowObject.getPossibleTriggerNames();
        },
        nodesByZone() {
            return this.flowObject.getNodesByZone();
        },
        taskNodes() {
            return this.flowObject.getNodesForZone('tasks');
        },
        activeEasNodes() {
            // TODO - make this less fragile!
            return this.flowObject.getNodesForZone('tasks').filter(n => n.is_active && n.key.startsWith('followup_reminder'));
        },
        loadingTransitions() {
            return this.flowObject.loadingTransitions;
        },
        transitionsByZone() {
            return this.flowObject.getTransitionsByZone();
        },
        actionTransitions() {
            return this.flowObject.getTransitionsForZone('actions');
        },
        allTransitions() {
            return this.flowObject.transitions;
        },
        currentTuId() {
            return this.$currentUserInfo?.tenant_user_id || '';
        },
        currentTuIsCompItemAdmin() {
            // updates after we get the flow object data
            return this.flowObjectData?.tu_object_role_slugs?.includes('admin') || false;
        },
        canManageItem() {
            // TODO - change this!  just hiding it for most people for now until fully ready
            return this.$currentUserInfo.user_is_staff || this.currentTuIsCompItemAdmin;  // add later, once we fully release this || this.currentTuIsTenantSuperadmin;
        },
        currentTuIsFullUser() {
            return !!this.$currentUserInfo?.is_paid_seat;
        },
        currentTuIsTenantAdmin() {
            return !!this.$currentUserInfo?.is_tenant_admin;
        },
        currentTuIsTenantSuperadmin() {
            return !!this.$currentUserInfo?.is_tenant_superadmin;
        },
        vendorStatusGroups() {
            // return a list of objects with the status group and the count of vendors in that group
            // this will be a computed property on the VCI model
            const groupObj = groupBy(this.vciStatusList, 'overall_status.key');
            // then return array of objects with {key, label, variant, count}
            return Object.entries(groupObj).map(([key, statusList]) => {
                return {
                    key: key,
                    label: statusList[0].overall_status.label,  // .overall_status is provided by backend! so we don't need all submissions
                    variant: statusList[0].overall_status.variant,
                    count: statusList.length,
                };
            });
        },
    },
    created() {
        this.getDetail();
        this.getVciStatusList();
        this.flowObject = new FlowObject({id: this.compItemId, model_class: 'ComplianceItem', data: {}});  // set in created hook?
    },
    mounted() {
        // this.flowObject?.fetchAll();  // this is async -- just need nodes instead???  probably transitions too.... just get all for now
        this.loadFlowObjectDataAndNodes();
        // TEMP
        console.log('CompItemQuickView mounted for compItemId', this.compItemId);
        window.compItemDetail = this;
    },
    methods: {
        async refresh() {
            return Promise.all([
                this.getDetail(),
                this.getVciStatusList(),
                //this.flowObject?.fetchAll(),
                this.loadFlowObjectDataAndNodes(),
            ]);
            // NOTE: currently, transitions are loaded separately when the dropdown is clicked - you may want to change that at some point
        },
        async loadFlowObjectDataAndNodes() {
            await Promise.all([
                this.flowObject.fetchData(),
                this.flowObject.fetchNodes(),
                //this.flowObject.fetchTransitions(),
            ]);
            //this.transitionsLoaded = true;
        },
        async loadTransitions() {  // call this separately, if/when actions dropdown is clicked
            this.transitionsLoaded = false;
            await this.flowObject.fetchTransitions();
            this.transitionsLoaded = true;
        },
        async getDetail() {
            // NOT NEEDED IF WE PASS IN EVERYTHING WE NEED
            this.loading = true;
            let resp;
            try {
                resp = await suppliosApi.compItemStd.get_item(this.compItemId);
                // this.compItem = new ComplianceItem(resp.data);  // resp.data;
            } catch (e) {
                this.apiErrorHandler(e);
            }
            if (resp.data) {
                this.compItem = new ComplianceItem(resp.data);  // resp.data.comp_item ???
                //this.vciStatusList = resp.data.status_list.map(s => new VendorCompItemStatus(s));
                // this.showEasManager = false;  // do we need to change this here?
            }
            console.log(`Finished getDetail for compItemId ${this.compItemId}`);
            this.loading = false;
        },
        async getVciStatusList() {
            // get the list of VendorCompItemStatus objects from the API (for this CompItem)
            this.loadingStatusList = true;
            let resp;
            try {
                // const config = {
                //     params: {
                //         include_empty: false,
                //         with_submissions: false,
                //         with_eas: false,
                //     }
                // };
                // resp = await suppliosApi.compItemStd.list_vendor_comp_item_status_for_comp_item(this.compItemId, config);
                resp = await suppliosApi.compItemStd.list_vendor_comp_items({
                    comp_item_ids: [this.compItemId],
                    with_submissions: false,
                    only_expiring: false,
                    only_in_progress: false,
                    include_empty: false,
                    with_eas: false,
                    with_tasks: false,  // don't need tasks for this, its just for a summary
                });
                this.vciStatusList = resp.data.status_list.map(s => new VendorCompItemStatus(s));
            } catch (e) {
                this.apiErrorHandler(e);
            }
            console.log(`Finished getVciStatusList for compItemId ${this.compItemId}`);
            this.loadingStatusList = false;
            this.statusListLoaded = true;
        },
        formatDate(dateString, defaultText = '-') {
            return dateString ? FormatDateHelper(dateString) : defaultText;
        },
        isFutureDate(dateString) {
            if (!dateString) {
                return true;  // we return true here so things say 'Expires' if not set
            }
            return isFutureDate(dateString);
        },
        get_eas_text(easData) {
            // return a string with the text of the easData
            // this will be a computed property on the EAS model
            const durationObj = {
                days: easData.duration_days,
                weeks: easData.duration_weeks,
                months: easData.duration_months,
            };
            const luxonDuration = Duration.fromObject(durationObj);
            const durationString = luxonDurationToHumanizedString(luxonDuration);
            return `Will start reminder task ${durationString} before expiration`;
        },
        handleExpireActionManagerUpdated() {
            this.refresh();
            this.emitUpdated();
        },
        emitUpdated() {
            // tell parent we updated something so it can refresh all
            this.$emit('updated');
        },
        // calling transitions
        startTransition(trans) {
            // get detail, show modal
            console.log('startTransition', trans);
            this.activeTransitionTrigger = trans.trigger;
            // let this.activeTransition be set by the form component
            this.transitionModalShowing = true;  // then when it opens, we will start the form
        },
        handleTransitionModalShown() {
            console.log('handleTransitionModalShow');
            // now autoStart: this.$refs.transitionForm.start();
        },
        handleTransitionFormEvent(payload) {
            console.log('handleTransitionFormEvent', payload);
            const { event, trigger, activeTransition, error, responseData } = payload;
            this.activeTransition = activeTransition;
            console.log('handleTransitionFormEvent - event, trigger, activeTransition, error, responseData', event, trigger, activeTransition, error, responseData);
            if (event === 'transition-success') {
                // refresh the data
                this.transitionModalShowing = false;
                this.refresh();
            }
        },
        handleTransitionFormCancel(payload) {
            console.log('handleTransitionFormCancel', payload);
            this.transitionModalShowing = false;
        },
        handleTransitionModalHidden() {
            this.activeTransition = {};
            this.activeTransitionTrigger = '';
        }
    },
}
</script>

<style lang="scss">

.comp-item-quick-view {
    .comp-item-quick-view-sections {
        display: flex;
        flex-wrap: wrap;
        flex-direction: column;
        width: 100%;
        column-gap: 1rem;
        row-gap: 1.5rem;

        .section-item {
            display: flex;
            width: 100%;
            column-gap: 0.75rem;
            row-gap: 1rem;
            padding-top: 1.5rem;
            &:not(:first-of-type) {
                border-top: 1px solid var(--gray-200);
            }

            .item-heading {
                text-transform: uppercase;
                font-weight: 500;
                font-size: 0.75rem;
                line-height: 1;
                margin-bottom: 0.5rem;
                color: var(--gray-500);
            }
            .item-text {
                font-weight: 500;
            }
        }
    }
}
</style>
